import * as React from "react";
import PropTypes from "prop-types";
import { styled, css } from "@mui/system";
import { Modal as BaseModal } from "@mui/material";
import Fade from "@mui/material/Fade";
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Alert,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  CreateSafePaySession,
  resetPayment,
} from "../../../redux/reducer/SafePayPayment";
import PaymentIcon from "@mui/icons-material/Payment";
import SecurityIcon from "@mui/icons-material/Security";
import moment from "moment";

export default function SafePayModal({
  open,
  onClose,
  billType,
  amount,
  billData,
  tenantId,
  landlordId,
  flatId,
  dueDate,
  quote,
}) {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.safepay);
  const [paymentInitiated, setPaymentInitiated] = React.useState(false);

  const handleClose = () => {
    dispatch(resetPayment());
    setPaymentInitiated(false);
    onClose();
  };

  const getBillTypeName = (type) => {
    const names = {
      kelectric: "K-Electric Bill",
      ssgc: "SSGC Gas Bill",
      water: "Water Bill",
      maintainance: "Maintenance Charges",
      trash: "Trash Charges",
      rent: "Monthly Rent",
      security: "Security Charges",
      fixedCharges: "Fixed Charges (Water, Security, Trash)",
    };
    return names[type] || type;
  };

  const handlePayWithSafePay = async () => {
    setPaymentInitiated(true);

    const paymentData = {
      amount: parseFloat(amount),
      billType: billType,
      billId: billData?._id || `${billType}_${Date.now()}`,
      tenantId: tenantId || localStorage.getItem("user_id"),
      landlordId: landlordId,
      flatId: flatId,
      description: `Payment for ${getBillTypeName(billType)} - ${moment().format("MMMM YYYY")}`,
    };

    try {
      const result = await dispatch(CreateSafePaySession({ paymentData }));

      if (result?.payload?.data?.checkoutUrl) {
        // Store payment info in localStorage for verification after redirect
        localStorage.setItem(
          "pending_payment",
          JSON.stringify({
            billType,
            amount,
            tenantId: paymentData.tenantId,
            landlordId,
            flatId,
            timestamp: Date.now(),
          })
        );

        // Redirect to SafePay checkout
        window.location.href = result.payload.data.checkoutUrl;
      } else {
        setPaymentInitiated(false);
      }
    } catch (err) {
      
      setPaymentInitiated(false);
    }
  };

  return (
    <Modal
      aria-labelledby="safepay-modal-title"
      open={open}
      onClose={handleClose}
      closeAfterTransition
      slots={{ backdrop: StyledBackdrop }}
    >
      <Fade in={open}>
        <ModalContent>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography variant="h5" fontWeight="bold">
              Pay with SafePay
            </Typography>
            <SecurityIcon sx={{ color: "#4CAF50", fontSize: 30 }} />
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Bill Details */}
          <Box sx={{ mb: 3, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Bill Type
            </Typography>
            <Typography variant="h6" sx={{ mb: 1 }}>
              {getBillTypeName(billType)}
            </Typography>

            <Typography variant="subtitle2" color="text.secondary">
              Amount to Pay
            </Typography>
            <Typography
              variant="h4"
              sx={{ color: "#1976d2", fontWeight: "bold" }}
            >
              PKR {parseFloat(amount || 0).toLocaleString()}
            </Typography>

            {quote && (quote.penalty > 0 || quote.commission > 0) && (
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Bill: PKR {Number(quote.base).toLocaleString()}
                </Typography>
                {quote.penalty > 0 && (
                  <Typography variant="body2" color="error">
                    Late fee: PKR {Number(quote.penalty).toLocaleString()}
                  </Typography>
                )}
                {quote.commission > 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Service fee: PKR {Number(quote.commission).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}

            {dueDate && (
              <>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Due Date
                </Typography>
                <Typography variant="body1">{dueDate}</Typography>
              </>
            )}
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* SafePay Info */}
          <Box sx={{ mb: 3, p: 2, bgcolor: "#e3f2fd", borderRadius: 2 }}>
            <Typography variant="body2" sx={{ display: "flex", gap: 1 }}>
              <SecurityIcon fontSize="small" color="primary" />
              Secure payment powered by SafePay. Your payment information is
              encrypted and secure.
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={loading || paymentInitiated}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handlePayWithSafePay}
              disabled={loading || paymentInitiated || !amount || amount <= 0}
              startIcon={
                loading || paymentInitiated ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <PaymentIcon />
                )
              }
              sx={{
                bgcolor: "#00C853",
                "&:hover": { bgcolor: "#00A843" },
                minWidth: 180,
              }}
            >
              {loading || paymentInitiated ? "Processing..." : "Pay Now"}
            </Button>
          </Box>

          {/* Payment Methods Info */}
          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="caption" color="text.secondary">
              Supports Credit/Debit Cards, Bank Transfer, JazzCash, Easypaisa
            </Typography>
          </Box>
        </ModalContent>
      </Fade>
    </Modal>
  );
}

SafePayModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  billType: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  quote: PropTypes.shape({
    base: PropTypes.number,
    penalty: PropTypes.number,
    commission: PropTypes.number,
    total: PropTypes.number,
  }),
  billData: PropTypes.object,
  tenantId: PropTypes.string,
  landlordId: PropTypes.string,
  flatId: PropTypes.string,
  dueDate: PropTypes.string,
};

const Backdrop = React.forwardRef((props, ref) => {
  const { open, ...other } = props;
  return (
    <Fade in={open}>
      <div ref={ref} {...other} />
    </Fade>
  );
});

Backdrop.propTypes = {
  open: PropTypes.bool,
};

const grey = {
  50: "#F3F6F9",
  100: "#E5EAF2",
  200: "#DAE2ED",
  300: "#C7D0DD",
  400: "#B0B8C4",
  500: "#9DA8B7",
  600: "#6B7A90",
  700: "#434D5B",
  800: "#303740",
  900: "#1C2025",
};

const Modal = styled(BaseModal)`
  position: fixed;
  z-index: 1300;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledBackdrop = styled(Backdrop)`
  z-index: -1;
  position: fixed;
  inset: 0;
  background-color: rgb(0 0 0 / 0.5);
  -webkit-tap-highlight-color: transparent;
`;

const ModalContent = styled("div")(
  ({ theme }) => css`
    font-family: "IBM Plex Sans", sans-serif;
    font-weight: 500;
    text-align: start;
    position: relative;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background-color: ${theme?.palette?.mode === "dark" ? grey[900] : "#fff"};
    border-radius: 12px;
    border: 1px solid ${theme?.palette?.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0 4px 20px
      ${theme?.palette?.mode === "dark"
        ? "rgb(0 0 0 / 0.5)"
        : "rgb(0 0 0 / 0.15)"};
    padding: 24px;
    color: ${theme?.palette?.mode === "dark" ? grey[50] : grey[900]};
    min-width: 400px;
    max-width: 500px;

    @media (max-width: 600px) {
      min-width: 90vw;
      padding: 16px;
    }
  `
);
